// components/Toolbox.js
import React from "react";
import {
    Box,
    Typography,
    Grid,
    Button as MaterialButton,
    Tooltip,
    Divider
} from "@material-ui/core";
import { Element, useEditor } from "@craftjs/core";
import {
    TextFields as TextIcon,
    ViewDay as SectionIcon,
    CheckBoxOutlineBlank as ContainerIcon,
    Image as ImageIcon,
    Remove as DividerIcon,
    ViewColumn as FlexboxIcon,
    Title as HeadingIcon,
    Subject as ParagraphIcon,
    TouchApp as ButtonIcon
} from '@material-ui/icons';

import { Container } from "./user/Container";
import { Text } from "./user/Text";
import { Section } from "./user/Section";
import { Button } from "./user/Button";
import { Image } from "./user/Image";
import { Divider as CustomDivider } from "./user/Divider";
import { Flexbox } from "./user/Flexbox";
import { Heading } from "./user/Heading";
import { Paragraph } from "./user/Paragraph";

export const Toolbox = () => {
    const { connectors } = useEditor();

    return (
        <Box>
            {/* Layout Components */}
            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ pl: 1 }}>
                    Layout
                </Typography>
                <Grid container spacing={1}>
                    {/* Container */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Container" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Container}
                                        padding={20}
                                        background="#ffffff"
                                        canvas
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<ContainerIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Container
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Flexbox */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Flexbox" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Flexbox}
                                        flexDirection="row"
                                        padding={20}
                                        canvas
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<FlexboxIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Flexbox
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Section */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Section" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Section}
                                        padding={20}
                                        backgroundColor="#f5f5f5"
                                        canvas
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<SectionIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Section
                            </MaterialButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ mt: 2 }} />

            {/* Basic Components */}
            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ pl: 1 }}>
                    Components
                </Typography>
                <Grid container spacing={1}>
                    {/* Heading */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Heading" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Heading}
                                        text="New Heading"
                                        fontSize={32}
                                        color="#000000"
                                        textAlign="left"
                                        level={2}
                                        margin={16}
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<HeadingIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Heading
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Paragraph */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Paragraph" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Paragraph}
                                        text="Start typing your paragraph here..."
                                        fontSize={16}
                                        color="#000000"
                                        textAlign="left"
                                        lineHeight={1.6}
                                        margin={16}
                                        maxWidth="75ch"
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<ParagraphIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Paragraph
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Text */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Text" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Text}
                                        text="New Text"
                                        fontSize={16}
                                        textAlign="left"
                                        fontWeight="normal"
                                        color="#000000"
                                        margin={0}
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<TextIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Text
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Button */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Button" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Button}
                                        text="Click Me"
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        margin={0}
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<ButtonIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Button
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Image */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Image" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={Image}
                                        src="/api/placeholder/400/300"
                                        alt="New Image"
                                        width={320}
                                        height={240}
                                        borderRadius={0}
                                        margin={0}
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<ImageIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Image
                            </MaterialButton>
                        </Tooltip>
                    </Grid>

                    {/* Divider */}
                    <Grid item xs={6}>
                        <Tooltip title="Add Divider" placement="top">
                            <MaterialButton
                                ref={ref => connectors.create(ref,
                                    <Element
                                        is={CustomDivider}
                                        color="#000000"
                                        thickness={1}
                                        margin={10}
                                    />
                                )}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<DividerIcon />}
                                size="small"
                                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1, px: 2, cursor: 'grab' }}
                            >
                                Divider
                            </MaterialButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
                    Drag and drop components to build your layout
                </Typography>
            </Box>
        </Box>
    );
};